/* eslint-disable object-shorthand */
/* eslint-disable prefer-destructuring */
/**
 * Main application charts
 */
import "./style.css";
import MDBox from "components/Basics/MDBox";
import DashboardLayout from "components/Advanced/LayoutContainers/DashboardLayout";
import DashboardNavbar from "components/Advanced/Navbars/DashboardNavbar";
import { useEffect, useState, useCallback, memo } from "react";
import { useDispatch, useSelector } from "react-redux";
import ChartsActions from "redux-react/actions/chartsActions";
import SettingsActions from "redux-react/actions/settingsActions";
import { Divider, GridLegacy as Grid, Icon, Tooltip, Fade, Fab } from "@mui/material";
import { useMaterialUIController } from "context";
import ChartsLoader from "components/Custom/ChartsLoader";
import { getLocalStorageBackValues } from "components/Custom/Filters/filters";
import { wsEvent } from "redux-react/actions/ws";
import { socket } from "redux-react/middleware/ws";
import i18n from "i18n";
import lod_ from "lodash";
import { display } from "redux-react/reducers/snackBarReducer";
import CrossTable from "./crossTable";
import FloatingActionsMenu from "./Components/FloatingActionsMenu";
import ChartEditingOrder from "./Components/ChartEditingOrder";
import OneValueDisplay from "./oneValueChart/display";
import ProportionChartDisplay from "./proportionChart/display";
import DatasetViewPannel from "./Components/DatasetViewPannel";
import { queryDictionary } from "helpers/dictionary";
import MDTypography from "components/Basics/MDTypography";
import useFilters from "hooks/useFilters";

/**
 * Default component to display charts, works with dynamic datas
 */
export default function ChartsPage({
	route,
	// Chart
	handleEditChart: handleEditChartProps,
	handleAddChart: handleAddChartProps
}) {
	const UPDATE_PAGE_CHART = `updatePageChart_${route.route}`;

	const dispatch = useDispatch();
	const profile = useSelector(state => state.profile);
	const filters = useSelector(state => state.filters);
	const [controller] = useMaterialUIController();
	const { darkMode } = controller;
	// Page Charts
	const [charts, setCharts] = useState([]);

	// Copy to detect changes
	// null for the first load
	const [pageFiltersCopy, setPageFiltersCopy] = useState(undefined);
	const [hasPageFiltersChanged, setHasPageFiltersChanged] = useState(false);

	// Generate filters components
	const { component: filtersComponents, loaded: filtersLoaded, pageFilters } = useFilters(route);

	/**
	 * Detect changes in page filters
	 */
	const detectFiltersChange = () => {
		// Wait for filters to be loaded
		if (filtersLoaded !== true) {
			setHasPageFiltersChanged(false);
			return;
		}

		const isEqual = lod_.isEqual(pageFilters, pageFiltersCopy);

		setHasPageFiltersChanged(!isEqual);
	};

	/**
	 * Get charts data to be displayed in front
	 */
	const getChartsData = typeList => {
		const filteredCharts = charts.filter(chart =>
			typeList.includes(chart.customType || chart.type)
		);
		const sortedCharts = filteredCharts.sort((a, b) => a.display.rank - b.display.rank);
		return sortedCharts;
	};

	const setPlaceHolderCharts = useCallback(charts => {
		setCharts(charts);
	}, []);

	const updatePageChart = useCallback(chart => {
		setCharts(prevCharts => {
			return prevCharts.map(pChart => {
				if (pChart.code === chart.code) {
					return {
						...pChart,
						data: chart.data ?? [],
						loaded: true,
						dictionary: chart.dictionary,
						resource: chart.resource
					};
				}
				return pChart;
			});
		});
	}, []);

	/**
	 * Reorder the charts
	 * @param {<string>} type - Types of the charts
	 * @param {number} from - From index
	 * @param {number} to - To index
	 */
	function handleReorderChart(type, from, to) {
		// 1- Get the current charts
		const currentCharts = getChartsData(type);

		// 2- Clone the current charts
		const reorderedCharts = lod_.cloneDeep(currentCharts);
		// 3- Swap the charts
		const [removed] = reorderedCharts.splice(from, 1);
		reorderedCharts.splice(to, 0, removed);

		// 4- Get the codes of the reordered charts
		const reorderedChartsCodes = reorderedCharts.map(chart => chart.code);

		// 5- Set the rank of the reordered charts
		const reorderedChartsWithRank = reorderedCharts.map((chart, index) => {
			// Set the rank
			chart.display.rank = index;
			// Update the chart in the backend
			dispatch(
				SettingsActions.editSetting(
					profile.assistantID,
					"chart",
					{
						code: chart.code,
						"display.rank": index
					},
					() => {}
				)
			);

			return chart;
		});

		// 6- Update the charts
		setCharts(prevCharts => {
			return prevCharts.map(pChart => {
				if (reorderedChartsCodes.includes(pChart.code)) {
					return reorderedChartsWithRank.find(chart => chart.code === pChart.code);
				}
				return pChart;
			});
		});

		// reorder in back
	}

	/**
	 * Drag handler
	 * @param {*} param0
	 * @returns
	 */
	const dragHandler = useCallback(
		({ type, from, to }) => {
			if (from === to) return;
			handleReorderChart(type, from, to);
		},
		[charts]
	);

	const loadCharts = () => {
		dispatch(
			wsEvent({
				event: "getChartsForPage",
				payload: {
					isSystem: route.system ?? false,
					route: route.route,
					filterByAgent: route?.filterByAgent ?? false,
					wsEventResult: UPDATE_PAGE_CHART,
					filters: pageFilters
				}
			})
		);
	};

	const handleDeleteChart = useCallback(
		code => {
			const onSuccess = res => {
				dispatch(
					display({
						message: i18n.t("SETTINGS.CHARTS.SUCCESS.delete"),
						type: "success"
					})
				);
				loadCharts();
			};
			dispatch(
				SettingsActions.deleteSetting(profile.assistantID, "chart", { code: code }, onSuccess)
			);
		},
		[dispatch, profile.assistantID]
	);

	const handleEditChart = useCallback(
		code => {
			handleEditChartProps(code, (editedChart, edit) => {
				if (edit) {
					const onSuccess = res => {
						dispatch(
							display({
								message: i18n.t("SETTINGS.CHARTS.SUCCESS.edit"),
								type: "success"
							})
						);
						loadCharts();
					};
					dispatch(
						SettingsActions.editSetting(profile.assistantID, "chart", editedChart, onSuccess)
					);
				}
			});
		},
		[handleEditChartProps, dispatch, profile.assistantID]
	);

	const handleAddChart = useCallback(() => {
		handleAddChartProps(
			{
				"display.pages": [route.route.replace("/", "")]
			},
			(editedChart, edit) => {
				const onSuccess = res => {
					dispatch(
						display({
							message: i18n.t("SETTINGS.CHARTS.SUCCESS.add"),
							type: "success"
						})
					);
					loadCharts();
				};
				dispatch(SettingsActions.newSetting(profile.assistantID, "chart", editedChart, onSuccess));
			}
		);
	}, []);

	useEffect(() => {
		if (filtersLoaded === true && pageFiltersCopy === undefined) {
			// First load
			setPageFiltersCopy(lod_.cloneDeep(pageFilters));
		}
		detectFiltersChange();
	}, [pageFilters, filtersLoaded, pageFiltersCopy]);

	useEffect(() => {
		setCharts([]);
		setPageFiltersCopy(undefined);
		/**
		 * WS Event
		 */
		socket.on(UPDATE_PAGE_CHART, updatePageChart);
		socket.on(`${UPDATE_PAGE_CHART}Placeholder`, setPlaceHolderCharts);

		return () => {
			socket.off(UPDATE_PAGE_CHART, updatePageChart);
			socket.off(`${UPDATE_PAGE_CHART}Placeholder`, setPlaceHolderCharts);

			socket.emit(`stopChartsProcessing`, {
				wsEventResult: UPDATE_PAGE_CHART
			});
		};
	}, [profile.selectedAssistant.assistantID, route.route]);

	useEffect(() => {
		if (filtersLoaded === true) {
			loadCharts();
		}
	}, [filtersLoaded]);

	/* Charts loader */
	if (!filtersLoaded) {
		return (
			<DashboardLayout>
				<MDBox>
					<DashboardNavbar />
					<ChartsLoader darkMode={darkMode} />
				</MDBox>
			</DashboardLayout>
		);
	} else {
		/* Main component */
		return (
			<DashboardLayout>
				{/*  */}
				<Fade in={hasPageFiltersChanged}>
					<div
						style={{
							position: "fixed",
							bottom: 20,
							right: 20,
							zIndex: 1500
						}}
					>
						<Fab
							variant="extended"
							color="info"
							className="fab-glow"
							onClick={() => {
								// Save current page filters as copy
								setPageFiltersCopy(lod_.cloneDeep(pageFilters));
								setHasPageFiltersChanged(false);
								loadCharts();
							}}
						>
							<MDTypography variant="h6" color="light">
								{i18n.t("SETTINGS.RULES.detectedModifications")}
							</MDTypography>
							<MDBox
								bgColor="light"
								p={1}
								borderRadius="lg"
								fontWeight="bold"
								display="flex"
								alignItems="center"
								ml={2}
							>
								<Icon sx={{ mr: 1 }} fontSize="medium">
									save
								</Icon>
								&nbsp;{i18n.t("SETTINGS.apply")}
							</MDBox>
						</Fab>
					</div>
				</Fade>

				<MDBox>
					<DashboardNavbar
						filters={[
							<MDBox display="flex">
								{route?.filterByAgent && (
									<Tooltip placement="top" title={i18n.t("SETTINGS.PAGES.filterByAgentPage")}>
										<Icon fontSize="large">person</Icon>
									</Tooltip>
								)}
								{filtersComponents}
							</MDBox>
						]}
					/>

					<MDBox mt={4}>
						{/* One value */}
						<OneValueDisplay
							charts={charts}
							setCharts={setCharts}
							pageEditMode={false}
							getChartsData={getChartsData}
							dragHandler={dragHandler}
							handleEditChart={handleEditChart}
							handleDeleteChart={handleDeleteChart}
							clickHandler={() => {}}
						/>
						{/* Proportion graphs */}
						<ProportionChartDisplay
							pageEditMode={false}
							getChartsData={getChartsData}
							dragHandler={dragHandler}
							handleEditChart={handleEditChart}
							handleDeleteChart={handleDeleteChart}
							clickHandler={() => {}}
						/>
						{/* Cross Tables */}
						<CrossTableDisplay
							pageEditMode={false}
							getChartsData={getChartsData}
							dragHandler={dragHandler}
							handleEditChart={handleEditChart}
							handleDeleteChart={handleDeleteChart}
							clickHandler={() => {}}
						/>
					</MDBox>
				</MDBox>
			</DashboardLayout>
		);
	}
}

/**
 * Cross table display
 * @returns
 */
const CrossTableDisplay = memo(
	({ pageEditMode, getChartsData, dragHandler, handleEditChart, handleDeleteChart }) => {
		const LIST = ["crossTable", "timeSeries", "multiCollection"];

		const [hoveredChart, setHoveredChart] = useState(null);

		const editingMouseInCard = code => {
			setHoveredChart(code);
		};

		const editingMouseOutCard = code => {
			setHoveredChart(null);
		};

		const isHovered = code => {
			return hoveredChart === code;
		};

		return (
			<>
				<MDBox display="flex" flexDirection="row">
					{pageEditMode && (
						<ChartEditingOrder
							getChartsData={getChartsData}
							type={LIST}
							dragHandler={dragHandler}
							handleEditChart={handleEditChart}
							mouseIn={editingMouseInCard}
							mouseOut={editingMouseOutCard}
							handleDeleteChart={handleDeleteChart}
						/>
					)}
					<MDBox
						flex="5"
						style={{
							width: "100%",
							overflowX: "auto"
						}}
					>
						<Grid container spacing={3} columns={{ xs: 2, sm: 2, md: 4, xxl: 6 }}>
							{getChartsData(LIST).map((chart, index) => {
								return (
									<CrossTable
										id={chart.code}
										chart={chart}
										data={chart.data}
										key={index}
										mt={5}
										xs={2}
										md={4}
										lg={2}
										xxl={6}
										style={{
											filter: isHovered(chart.code) ? "brightness(0.8)" : ""
										}}
									/>
								);
							})}
						</Grid>
					</MDBox>
				</MDBox>
				{pageEditMode && getChartsData(LIST).length > 0 && <Divider mt={10} mb={10} />}
			</>
		);
	}
);
